import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

import { GoogleLogo, Eye, EyeSlash } from '@phosphor-icons/react';

export default function Register() {
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    name: '', 
    display_name: '',
    institution: '',
    target_exam: '' 
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await register(formData);
      navigate('/dashboard');
    } catch (err) {
      let msg = err.response?.data?.detail;
      if (Array.isArray(msg)) msg = msg.map(m => m.msg).join(' ');
      setError(msg || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden py-12">
      <Card className="w-full max-w-md z-10 rounded-none border-white/10 bg-black/60 backdrop-blur-xl">
        <CardHeader className="text-center space-y-2">
          <CardTitle className="font-heading text-4xl">New Recruit</CardTitle>
          <CardDescription className="text-muted-foreground uppercase tracking-widest font-bold text-xs">Register for the elite exam preparation hub</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="text-destructive text-sm font-bold bg-destructive/10 p-3 border border-destructive/20">{error}</div>}
            
            <Input placeholder="FULL NAME" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required className="h-12 bg-black/50 border-white/10 rounded-none tracking-wider" data-testid="reg-name" />
            
            <Input placeholder="DISPLAY NAME / ANONYMOUS ALIAS (OPTIONAL)" value={formData.display_name} onChange={e => setFormData({...formData, display_name: e.target.value})} className="h-12 bg-black/50 border-white/10 rounded-none tracking-wider" data-testid="reg-display-name" />
            
            <Input type="email" placeholder="EMAIL ADDRESS" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required className="h-12 bg-black/50 border-white/10 rounded-none tracking-wider text-white" data-testid="reg-email" />
            
            <div className="relative">
              <Input 
                type={showPassword ? "text" : "password"} 
                placeholder="PASSWORD" 
                value={formData.password} 
                onChange={e => setFormData({...formData, password: e.target.value})} 
                required 
                className="h-12 bg-black/50 border-white/10 rounded-none tracking-wider text-white pr-10" 
                data-testid="reg-password" 
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
              >
                {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
              </button>
            </div>
            
            <Input placeholder="COLLEGE / INSTITUTION (OPTIONAL)" value={formData.institution} onChange={e => setFormData({...formData, institution: e.target.value})} className="h-12 bg-black/50 border-white/10 rounded-none tracking-wider text-white" data-testid="reg-institution" />

            <Input placeholder="TARGET EXAM (e.g. JEE, NEET, IAS)" value={formData.target_exam} onChange={e => setFormData({...formData, target_exam: e.target.value})} required className="h-12 bg-black/50 border-white/10 rounded-none tracking-wider" data-testid="reg-exam" />
            
            <Button type="submit" className="w-full h-14 font-heading text-xl tracking-wider rounded-none bg-primary hover:bg-primary/80" data-testid="reg-submit">
              ENLIST NOW
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-black/60 px-2 text-muted-foreground font-bold tracking-widest">OR CONTINUE WITH</span>
              </div>
            </div>
            
            <Button type="button" onClick={handleGoogleLogin} variant="outline" className="w-full h-14 font-heading text-xl tracking-wider rounded-none border-border hover:bg-secondary flex items-center justify-center gap-2">
              <GoogleLogo size={24} weight="bold" /> GOOGLE
            </Button>

            <div className="text-center mt-4 space-y-4">
              <span className="text-muted-foreground text-sm tracking-widest">ALREADY ENLISTED? </span>
              <Link to="/login" className="text-primary text-sm font-bold tracking-widest hover:underline">LOGIN</Link>
              
              <div className="pt-4 border-t border-white/10">
                <Link to="/legal" className="text-xs text-muted-foreground hover:text-white transition-colors tracking-widest uppercase">Terms of Service & Privacy Policy</Link>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}